/**
 * Gauntlet Debates - A platform for structured, AI-moderated honorable debates
 * Copyright (c) 2025 Gauntlet Debates. All rights reserved.
 * 
 * This file is part of the Gauntlet Debates platform and is protected by 
 * copyright and other intellectual property laws. Unauthorized reproduction,
 * distribution, or disclosure of this file or its contents is prohibited.
 */
import React, { useState, useEffect } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Paper,
  Avatar,
  Grid,
  Divider,
  Card,
  CardContent,
  Button,
  Chip,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  CircularProgress,
  Tabs,
  Tab,
  useTheme,
  LinearProgress,
  Stack,
  Alert
} from '@mui/material';
import { format } from 'date-fns';
import {
  Person as PersonIcon,
  EmojiEvents as TrophyIcon,
  Public as PublicIcon,
  SportsScore as ScoreIcon,
  CheckCircle as WinIcon,
  VerifiedUser as VerifiedIcon,
  Email as EmailIcon
} from '@mui/icons-material';

import { getUserProfile, getUserByUsername, UserProfile } from '../services/userService';
import { useAuth } from '../contexts/AuthContext';
import logger from '../utils/logger';

// Format date to a nice readable format
const formatDate = (dateString: string | Date) => {
  if (!dateString) return 'Unknown date';
  const date = new Date(dateString);
  return format(date, 'MMM d, yyyy');
};

// Tab panel component for tabs
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`profile-tabpanel-${index}`}
      aria-labelledby={`profile-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const UserProfilePage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [resendingEmail, setResendingEmail] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const theme = useTheme();
  const { user, resendVerificationEmail } = useAuth();

  // Check if the current user is viewing their own profile
  const isOwnProfile = user && profile && (profile.isOwner === true);

  useEffect(() => {
    const loadProfile = async () => {
      if (!userId) {
        setError('No user ID provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        // Check if userId is a MongoDB ID or username
        const isMongoId = /^[0-9a-f]{24}$/i.test(userId);
        
        if (isMongoId) {
          // If it's a MongoDB ID, just fetch the profile directly
          const userProfile = await getUserProfile(userId);
          setProfile(userProfile);
        } else {
          // If it's a username, try to get the user ID first
          try {
            const userProfile = await getUserProfile(userId);
            setProfile(userProfile);
          } catch (error) {
            logger.error('Initial profile lookup failed, trying username lookup:', error);
            // If direct profile lookup fails, try to find the user by username
            const user = await getUserByUsername(userId);
            if (user && (user.id || user._id)) {
              // Then get the profile with the found ID
              const userProfile = await getUserProfile(user.id || user._id);
              setProfile(userProfile);
            } else {
              throw new Error(`User not found: ${userId}`);
            }
          }
        }
        setLoading(false);
      } catch (err: any) {
        logger.error('Error loading user profile:', err);
        setError(err.message || 'Failed to load user profile');
        setLoading(false);
      }
    };

    loadProfile();
  }, [userId]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleResendVerification = async () => {
    if (!isOwnProfile) return;
    
    setResendingEmail(true);
    try {
      await resendVerificationEmail();
      setResendSuccess(true);
      
      // Hide success message after 5 seconds
      setTimeout(() => {
        setResendSuccess(false);
      }, 5000);
    } catch (error) {
      logger.error('Error resending verification email:', error);
    } finally {
      setResendingEmail(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
          <CircularProgress />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading user profile...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error || !profile) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h5" color="error" gutterBottom>
            Error Loading Profile
          </Typography>
          <Typography variant="body1">
            {error || 'Unable to load user profile'}
          </Typography>
          <Button
            component={RouterLink}
            to="/"
            variant="contained"
            sx={{ mt: 3 }}
          >
            Return to Home
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
      {/* User Profile Header */}
      <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={2} sx={{ display: 'flex', justifyContent: { xs: 'center', md: 'flex-start' } }}>
            <Avatar
              src={profile.profileImageUrl}
              alt={profile.username}
              sx={{ 
                width: 120, 
                height: 120,
                backgroundColor: theme.palette.primary.main,
                fontSize: '3rem'
              }}
            >
              {profile.username?.[0]?.toUpperCase() || <PersonIcon fontSize="large" />}
            </Avatar>
          </Grid>
          <Grid item xs={12} md={7}>
            <Typography variant="h4" gutterBottom>
              {profile.username}
            </Typography>
            {profile.bio && (
              <Typography variant="body1" sx={{ mb: 1 }}>
                {profile.bio}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary">
              Member since {formatDate(profile.createdAt || new Date())}
            </Typography>
          </Grid>
          <Grid item xs={12} md={3}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'center', md: 'flex-end' } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <TrophyIcon sx={{ color: 'gold', mr: 1 }} />
                <Typography variant="h6">
                  Excalibur Rating: {profile.excaliburIndex}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {profile.winRate.toFixed(1)}% Win Rate
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Total Debates
              </Typography>
              <Typography variant="h4">
                {profile.totalDebatesCount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Debates Won
              </Typography>
              <Typography variant="h4">
                {profile.debatesWonCount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Public Debates
              </Typography>
              <Typography variant="h4">
                {profile.publicDebatesCount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Win Rate
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="h4" sx={{ mr: 1 }}>
                  {profile.winRate.toFixed(1)}%
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={Math.min(profile.winRate, 100)} 
                sx={{ height: 8, borderRadius: 4, mt: 1 }}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs for debates and other content */}
      <Paper sx={{ width: '100%' }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab label="Debates" />
          <Tab label="Achievements" />
          <Tab label="About" />
        </Tabs>

        {/* Debates Tab */}
        <TabPanel value={tabValue} index={0}>
          {profile?.debates && profile.debates.length > 0 ? (
            <List>
              {profile.debates.map((debate) => (
                <Paper 
                  key={debate.id} 
                  elevation={1} 
                  sx={{ 
                    mb: 2, 
                    borderLeft: debate.isWinner ? `4px solid ${theme.palette.success.main}` : 'none'
                  }}
                >
                  <ListItem
                    component={RouterLink}
                    to={`/debate/view/${debate.id}`}
                    sx={{ 
                      textDecoration: 'none', 
                      color: 'inherit',
                      transition: 'background-color 0.2s',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)'
                      }
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: debate.isWinner ? 'success.main' : 'primary.main' }}>
                        {debate.isWinner ? <WinIcon /> : <ScoreIcon />}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={debate.topic}
                      secondary={
                        <React.Fragment>
                          <Typography
                            component="span"
                            variant="body2"
                            color="text.primary"
                          >
                            {debate.status.charAt(0).toUpperCase() + debate.status.slice(1)}
                          </Typography>
                          {` — ${formatDate(debate.createdAt)}`}
                        </React.Fragment>
                      }
                    />
                    <Stack direction="row" spacing={1}>
                      {debate.isWinner && (
                        <Chip 
                          size="small" 
                          color="success" 
                          label="Winner" 
                          icon={<TrophyIcon />} 
                        />
                      )}
                      <Chip 
                        size="small" 
                        color={debate.status === 'completed' ? 'default' : 'primary'} 
                        label={debate.status.charAt(0).toUpperCase() + debate.status.slice(1)} 
                      />
                      {debate.isPublic !== undefined && (
                        <Chip
                          size="small"
                          color={debate.isPublic ? "info" : "default"}
                          label={debate.isPublic ? "Public" : "Private"}
                          icon={debate.isPublic ? <PublicIcon fontSize="small" /> : undefined}
                        />
                      )}
                    </Stack>
                  </ListItem>
                </Paper>
              ))}
            </List>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" color="text.secondary">
                {isOwnProfile 
                  ? "You haven't participated in any debates yet" 
                  : "No debates found for this user"}
              </Typography>
              <Button
                component={RouterLink}
                to="/debates"
                variant="contained"
                sx={{ mt: 2 }}
              >
                Browse Debates
              </Button>
            </Box>
          )}
        </TabPanel>

        {/* Achievements Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="h6" gutterBottom>
              Excalibur Level: {Math.floor(profile.excaliburIndex / 10)}
            </Typography>
            <LinearProgress 
              variant="determinate" 
              value={(profile.excaliburIndex % 10) * 10} 
              sx={{ height: 10, borderRadius: 5, maxWidth: 400, mx: 'auto', mb: 4 }} 
            />
            
            <Grid container spacing={2} justifyContent="center">
              <Grid item xs={12} sm={6} md={4}>
                <Card>
                  <CardContent>
                    <TrophyIcon sx={{ fontSize: 60, color: profile.debatesWonCount > 0 ? 'gold' : 'grey.400' }} />
                    <Typography variant="h6" gutterBottom>First Victory</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {profile.debatesWonCount > 0 ? 'Achieved' : 'Win your first debate'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} sm={6} md={4}>
                <Card>
                  <CardContent>
                    <PublicIcon sx={{ fontSize: 60, color: profile.publicDebatesCount >= 5 ? 'primary.main' : 'grey.400' }} />
                    <Typography variant="h6" gutterBottom>Public Speaker</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {profile.publicDebatesCount >= 5 ? 'Achieved' : `${profile.publicDebatesCount}/5 public debates`}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} sm={6} md={4}>
                <Card>
                  <CardContent>
                    <ScoreIcon sx={{ fontSize: 60, color: profile.winRate >= 60 ? 'success.main' : 'grey.400' }} />
                    <Typography variant="h6" gutterBottom>Skilled Debater</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {profile.winRate >= 60 ? 'Achieved' : 'Maintain a 60% win rate'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </TabPanel>

        {/* About Tab */}
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ px: 2 }}>
            <Typography variant="h6" gutterBottom>About</Typography>
            <Typography variant="body1" paragraph>
              {profile?.bio || `${profile?.username || 'This user'} hasn't added a bio yet.`}
            </Typography>
            
            <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>Member Information</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Username
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {profile?.username}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Member Since
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {formatDate(profile?.createdAt || new Date())}
                </Typography>
              </Grid>
              {isOwnProfile && (
                <Grid item xs={12}>
                  <Box sx={{ mt: 2, mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Email Verification Status
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      {user?.isEmailVerified ? (
                        <>
                          <VerifiedIcon sx={{ color: 'success.main', mr: 1 }} />
                          <Typography variant="body1">
                            Your email is verified
                          </Typography>
                        </>
                      ) : (
                        <>
                          <EmailIcon sx={{ color: 'warning.main', mr: 1 }} />
                          <Typography variant="body1" sx={{ mr: 2 }}>
                            Your email is not verified
                          </Typography>
                          <Button 
                            size="small" 
                            variant="outlined" 
                            color="primary"
                            onClick={handleResendVerification}
                            disabled={resendingEmail}
                            startIcon={resendingEmail ? <CircularProgress size={16} /> : <EmailIcon />}
                          >
                            Resend Verification Email
                          </Button>
                        </>
                      )}
                    </Box>
                    {resendSuccess && (
                      <Alert severity="success" sx={{ mt: 1 }}>
                        Verification email sent successfully! Please check your inbox.
                      </Alert>
                    )}
                  </Box>
                </Grid>
              )}
            </Grid>
            
            <Divider sx={{ my: 3 }} />
            
            <Typography variant="h6" gutterBottom>Excalibur Index Explained</Typography>
            <Typography variant="body2">
              The Excalibur Index is a measure of debate prowess calculated from wins, 
              public debate participation, and other factors. A higher index indicates 
              a more skilled debater.
            </Typography>
          </Box>
        </TabPanel>
      </Paper>
    </Container>
  );
};

export default UserProfilePage; 